import { Injectable } from '@angular/core';
import { BaseRequester } from './common/base-requester';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// Generic client for the App Config Store lambda (one JSON document per key
// in management-console.reordering, fetched and replaced whole). Typed
// services own their key and document shape on top of this: reordering
// settings ('default'), PurchaseOrderService ('purchase-orders'),
// RopOverrideService ('rop-overrides').
interface ConfigStoreRequest<T> {
    action: 'get' | 'set';
    key: string;
    config?: T;
}

export interface ConfigStoreResponse<T> {
    action: 'get' | 'set';
    key: string;
    config: T | null;
}

@Injectable({
    providedIn: 'root',
})
export class AppConfigStoreService extends BaseRequester {
    constructor() {
        super();
    }

    // Resolves null until the first set for this key.
    public get<T>(key: string): Observable<T | null> {
        const payload: ConfigStoreRequest<T> = { action: 'get', key };
        return this.internalPostRequest<ConfigStoreRequest<T>>(
            environment.reorderingConfigEndpoint,
            payload,
        ).pipe(
            map(
                (response) =>
                    this.serviceHandler<ConfigStoreResponse<T>>(response)
                        .config,
            ),
        );
    }

    // Upserts the whole document for the key (the lambda stores it verbatim,
    // no merge) — callers own read-modify-write.
    public set<T>(key: string, config: T): Observable<ConfigStoreResponse<T>> {
        const payload: ConfigStoreRequest<T> = { action: 'set', key, config };
        return this.internalPostRequest<ConfigStoreRequest<T>>(
            environment.reorderingConfigEndpoint,
            payload,
        ).pipe(
            map((response) =>
                this.serviceHandler<ConfigStoreResponse<T>>(response),
            ),
        );
    }
}
